import { create } from 'zustand';
import { api, ApiError } from '@/lib/api';
import { applySettings } from '@/lib/settings';
import type { LocationRow } from '@/stores/admin';

/** One place as the typeahead and the place page see it. */
export interface Place {
  id: string;
  name: string;
  state: string;
  tier: 1 | 2;
  postcode: string | null;
  parent_id: string | null;
  parent_name: string | null;
}

export interface PlaceDetail extends Place {
  timezone: string;
  /** The place's own position, a capital's being its city centre: where its Venues map opens. */
  lat: number | null;
  lng: number | null;
  /** `[w, s, e, n]` around the place's state: as far as its Venues map may be panned out. */
  state_bounds: [number, number, number, number] | null;
}

/** A colour pack as a visitor sees it: the name and the colour that outlines its cards. */
export interface PublicPack {
  name: string;
  colour: string;
}

interface LocationState {
  locations: LocationRow[];
  /** The admin's colour packs at the time of loading, in their order, for the legend. */
  packs: PublicPack[];
  status: 'idle' | 'loading' | 'ready';
  load: () => Promise<void>;
  find: (id: string | null | undefined) => LocationRow | null;
}

/**
 * The rail, fetched once per visit. The header sheet and the home rail both need it, and two
 * components each firing their own request is two rate-limit slots spent on one answer.
 *
 * It is the rail and not the gazetteer: there are fifteen thousand localities, so a place that
 * is not on the rail is looked up rather than listed — `searchPlaces` and `readPlace` below.
 */
export const useLocations = create<LocationState>((set, get) => ({
  locations: [],
  packs: [],
  status: 'idle',

  load: async () => {
    if (get().status !== 'idle') return;
    set({ status: 'loading' });
    try {
      const data = await api<{
        locations: LocationRow[];
        settings: Record<string, number | boolean>;
        packs: PublicPack[];
      }>('/locations');
      // The tuning values and the packs ride on this response rather than taking one of their
      // own: the first screen's request budget belongs to listings.
      applySettings(data.settings);
      set({ locations: data.locations, packs: data.packs ?? [], status: 'ready' });
    } catch {
      // A missing city list degrades to "All Australia" rather than an error screen: the grid
      // below it is still the thing the visitor came for.
      set({ status: 'ready' });
    }
  },

  find: (id) => (id ? (get().locations.find((location) => location.id === id) ?? null) : null),
}));

/**
 * The place lookup every screen that names a place shares.
 *
 * Not in the store: a typeahead's results belong to the box that asked for them, and caching
 * them site-wide would mean one box's stale answer arriving in another. The store holds the
 * rail, which is the same for everybody, and nothing else.
 */
export async function searchPlaces(
  query: string,
  tier?: 1 | 2,
  options: { primary?: boolean; secondary?: boolean; state?: string } = {},
): Promise<Place[]> {
  const term = query.trim();
  if (!term) return [];
  const params = new URLSearchParams({ q: term });
  if (tier) params.set('tier', String(tier));
  // The first place is never a CBD and the second is never a capital; both are in one state.
  if (options.primary) params.set('primary', '1');
  if (options.secondary) params.set('secondary', '1');
  if (options.state) params.set('state', options.state);
  const data = await api<{ locations: Place[] }>(`/locations/search?${params}`);
  return data.locations;
}

/**
 * One place in full. Null **only** when there is no such address, which is what makes /c/{x} a
 * 404; anything else — offline, refused, the server falling over — throws, because telling a
 * reader on a bad connection that Sydney does not exist is a lie the page cannot take back.
 */
export async function fetchPlace(id: string): Promise<PlaceDetail | null> {
  try {
    return await api<PlaceDetail>(`/locations/${encodeURIComponent(id)}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** `fetchPlace` for the callers that only want a name to print: any failure is simply no place. */
export function readPlace(id: string): Promise<PlaceDetail | null> {
  return fetchPlace(id).catch(() => null);
}

/** A state's capital, the CBD that counts as it, and the ranges that bound a secondary and a boost. */
export interface Capital {
  state: string;
  city_id: string;
  city_name: string;
  cbd_id: string | null;
  cbd_name: string | null;
  capital_range_km: number;
  regional_range_km: number;
}

let capitals: Promise<Capital[]> | null = null;

/**
 * The eight capitals. They change when an operator edits a range, which is rare, so one fetch per
 * visit is the right amount; a failed fetch is forgotten so the next form that wants them asks
 * again rather than inheriting the failure.
 */
export function readCapitals(): Promise<Capital[]> {
  capitals ??= api<{ capitals: Capital[] }>('/capitals')
    .then((data) => data.capitals)
    .catch(() => {
      capitals = null;
      return [] as Capital[];
    });
  return capitals;
}
